import { Button, ButtonProps, Text } from '@shared/components';
import { useToast } from '@shared/components/toast';
import { useSelectQuickReplyMutation } from '@shared/generated/graphql';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { parseQuickReplySelection } from 'clerk_common/cards/quickReply';
import {
  CardAction,
  CardActionStyle,
  CardResolution,
  formatCardResolution,
  InteractiveCard,
} from 'clerk_common/cards/types';
import { assertUnreachable } from 'clerk_common/enum';
import { useState } from 'react';

type QuickReplyButtonsProps = {
  card: InteractiveCard;
  conversationId?: string;
};

export const QuickReplyButtons = ({
  card,
  conversationId,
}: QuickReplyButtonsProps) => {
  const { sendToast } = useToast();
  const { track } = useAnalytics();
  const [selectQuickReply, { loading }] = useSelectQuickReplyMutation();
  const [localResolution, setLocalResolution] = useState<CardResolution>();
  const resolution = card.resolution ?? localResolution;

  if (resolution) {
    return (
      <Text type="body-xs" className="text-gray-500">
        {formatCardResolution(resolution)}
      </Text>
    );
  }

  const handlePress = async (action: CardAction) => {
    const selection = parseQuickReplySelection(action.data);
    if (!selection || !conversationId) {
      sendToast('Unable to send this reply.', { variant: 'error' });
      return;
    }
    try {
      const { data } = await selectQuickReply({
        variables: { input: { conversationId, ...selection } },
      });
      if (!data?.selectQuickReply) {
        setLocalResolution({ outcome: 'Already answered', at: '' });
        return;
      }
      setLocalResolution({ outcome: `Picked ${action.label}`, at: '' });
      track('Selected Quick Reply', {
        conversationId,
        data: { optionIndex: selection.optionIndex },
      });
    } catch (error) {
      sendToast('Unable to send this reply.', {
        variant: 'error',
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {card.actions.map((action) => (
        <Button
          key={action.label}
          size="small"
          {...toButtonStyleProps(action.style)}
          isDisabled={loading}
          onPress={() => handlePress(action)}
        >
          {action.label}
        </Button>
      ))}
    </div>
  );
};

type ButtonStyleProps = Pick<ButtonProps, 'variant' | 'isGhost' | 'isOutline'>;

function toButtonStyleProps(style?: CardActionStyle): ButtonStyleProps {
  switch (style) {
    case 'default':
      return { variant: 'default' };
    case 'subtle':
      return { variant: 'default', isGhost: true };
    case 'primary':
      return { variant: 'vibrant' };
    case 'secondary':
    case undefined:
      return { variant: 'secondary' };
    case 'success':
      return { variant: 'success' };
    case 'cancel':
      return { variant: 'danger', isOutline: true };
    case 'danger':
      return { variant: 'danger' };
    default:
      return assertUnreachable(style);
  }
}
